import { supabase } from './supabase';

export interface BankRow {
  id: number;
  exam_id: number;
  set_no: number;
  source_no: number | null;
  category: string | null;
  question: string;
  option1: string | null; option2: string | null; option3: string | null;
  option4: string | null; option5: string | null; option6: string | null;
  total_option: number | null;
  correct_ans: string | null;
  explanation: string | null;
}

export type BankInput = Omit<BankRow, 'id'>;

export interface BankFilter { examId: number; set?: number | null; category?: string; search?: string; page: number; pageSize: number }

const COLS = 'id, exam_id, set_no, source_no, category, question, option1, option2, option3, option4, option5, option6, total_option, correct_ans, explanation';
const RLS_HELP = 'Nothing was changed. Run supabase/question_bank_admin_policies.sql in the Supabase SQL editor to allow admin edits.';

export async function fetchBankPage(f: BankFilter): Promise<{ success: boolean; rows?: BankRow[]; total?: number; error?: string }> {
  let q = supabase.from('question_bank').select(COLS, { count: 'exact' }).eq('exam_id', f.examId);
  if (f.set) q = q.eq('set_no', f.set);
  if (f.category) q = q.eq('category', f.category);
  if (f.search && f.search.trim()) q = q.ilike('question', `%${f.search.trim().replace(/[%,]/g, ' ')}%`);
  const from = f.page * f.pageSize;
  const res = await q.order('set_no', { ascending: true }).order('source_no', { ascending: true }).range(from, from + f.pageSize - 1);
  if (res.error) return { success: false, error: res.error.message };
  return { success: true, rows: (res.data || []) as BankRow[], total: res.count ?? 0 };
}

/** Set and category counts for one exam (small: only two columns are read). */
export async function fetchBankSummary(examId: number): Promise<{ success: boolean; sets?: { set: number; count: number }[]; categories?: { name: string; count: number }[]; maxSourceNo?: number; error?: string }> {
  const sets = new Map<number, number>();
  const cats = new Map<string, number>();
  let maxNo = 0;
  for (let from = 0; ; from += 1000) {
    const res = await supabase.from('question_bank').select('set_no, category, source_no').eq('exam_id', examId).order('id').range(from, from + 999);
    if (res.error) return { success: false, error: res.error.message };
    const rows = res.data || [];
    for (const r of rows) {
      sets.set(r.set_no, (sets.get(r.set_no) || 0) + 1);
      const c = r.category || '(none)';
      cats.set(c, (cats.get(c) || 0) + 1);
      if (r.source_no && r.source_no > maxNo) maxNo = r.source_no;
    }
    if (rows.length < 1000) break;
  }
  return {
    success: true,
    sets: Array.from(sets.entries()).map(([set, count]) => ({ set, count })).sort((a, b) => a.set - b.set),
    categories: Array.from(cats.entries()).map(([name, count]) => ({ name, count })).sort((a, b) => a.name.localeCompare(b.name)),
    maxSourceNo: maxNo,
  };
}

export async function saveBankRow(id: number | null, input: BankInput): Promise<{ success: boolean; error?: string }> {
  if (id === null) {
    const { data, error } = await supabase.from('question_bank').insert(input).select('id');
    if (error) return { success: false, error: error.message };
    return data && data.length ? { success: true } : { success: false, error: RLS_HELP };
  }
  const { data, error } = await supabase.from('question_bank').update(input).eq('id', id).select('id');
  if (error) return { success: false, error: error.message };
  return data && data.length ? { success: true } : { success: false, error: RLS_HELP };
}

export async function deleteBankRows(ids: number[]): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.from('question_bank').delete().in('id', ids).select('id');
  if (error) return { success: false, error: error.message };
  return data && data.length ? { success: true } : { success: false, error: RLS_HELP };
}

export async function moveBankRows(ids: number[], patch: { set_no?: number; category?: string }): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.from('question_bank').update(patch).in('id', ids).select('id');
  if (error) return { success: false, error: error.message };
  return data && data.length ? { success: true } : { success: false, error: RLS_HELP };
}

/** Renames a category for every question of the exam; renaming to an existing name merges the two. */
export async function renameBankCategory(examId: number, from: string, to: string): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.from('question_bank').update({ category: to }).eq('exam_id', examId).eq('category', from).select('id');
  if (error) return { success: false, error: error.message };
  return data && data.length ? { success: true } : { success: false, error: RLS_HELP };
}

/** Every row matching the filter (all pages), for export. */
export async function fetchBankAll(f: Omit<BankFilter, 'page' | 'pageSize'>): Promise<{ success: boolean; rows?: BankRow[]; error?: string }> {
  const out: BankRow[] = [];
  for (let page = 0; ; page++) {
    const res = await fetchBankPage({ ...f, page, pageSize: 1000 });
    if (!res.success) return { success: false, error: res.error };
    out.push(...(res.rows || []));
    if ((res.rows || []).length < 1000) break;
  }
  return { success: true, rows: out };
}

const CSV_COLUMNS: (keyof BankRow)[] = ['exam_id', 'set_no', 'source_no', 'category', 'question', 'option1', 'option2', 'option3', 'option4', 'option5', 'option6', 'total_option', 'correct_ans', 'explanation'];
const csvCell = (v: unknown): string => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Same columns as the question_bank table (correct_ans = 1-based option numbers), with a BOM so Excel reads UTF-8. */
export const bankToCsv = (rows: BankRow[]): string =>
  '\uFEFF' + [CSV_COLUMNS.join(','), ...rows.map(r => CSV_COLUMNS.map(c => csvCell(r[c])).join(','))].join('\r\n');

export const bankToJson = (rows: BankRow[]): string => JSON.stringify(rows.map(({ id, ...rest }) => rest), null, 2);

export function downloadText(filename: string, text: string, mime: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: `${mime};charset=utf-8` }));
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
